import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Chip,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  CircularProgress,
  Paper,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { useAuth } from '../../contexts/AuthContext';
import { useUnidade } from '../../contexts/UnidadeContext';
import { saudeService } from '../../services/api';
import FonteDadosSelector, { getFonteInicial, FonteDados } from '../../components/saude/FonteDadosSelector';

/**
 * Lista nominal de saneamento cadastral do CVAT.
 *
 * Quatro universos, escolhidos por `?tipo=`; cada um responde a uma pergunta
 * operacional e traz as colunas especificas dela.
 */

const TIPOS: Record<string, { titulo: string; descricao: string }> = {
  'sem-vinculo-domiciliar': {
    titulo: 'Sem vínculo domiciliar',
    descricao:
      'Cadastro individual válido, sem cadastro domiciliar. Vincular ao domicílio dobra o peso da pessoa (0,75 → 1,5) sem nenhum atendimento novo — é a única alavanca do CVAT que não depende de produção assistencial.',
  },
  'a-vencer': {
    titulo: 'Cadastro a vencer',
    descricao:
      'Cadastros que completam 24 meses sem atualização. Ao vencer, a pessoa sai inteira do numerador — atualizar antes evita a perda.',
  },
  'sem-acompanhamento': {
    titulo: 'Sem acompanhamento',
    descricao:
      'Cadastrados que não têm "mais de um contato assistencial com ao menos uma prática de cuidado" em 12 meses. Ordenados por peso: um idoso ou criança beneficiário do Bolsa Família vale 2,5× uma pessoa sem critério.',
  },
  'identificacao-incompleta': {
    titulo: 'Identificação incompleta',
    descricao:
      'Cadastros sem CPF e sem CNS, sem equipe, ou com equipe cujo INE não está ativo no CNES. Passam no PEC e são rejeitados na validação do SISAB.',
  },
};

const maskCpf = (cpf?: string | null): string => {
  const d = (cpf || '').replace(/\D/g, '');
  if (d.length !== 11) return cpf || '—';
  return `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**`;
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s.split('-').reverse().join('/') : '—';
};

const SaudeCvatLista: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { municipioAtivo } = useAuth();
  const { unidadeSelecionada } = useUnidade();

  const tipo = searchParams.get('tipo') || 'sem-vinculo-domiciliar';
  const quadrimestre = searchParams.get('quadrimestre') || undefined;
  const meta = TIPOS[tipo] || TIPOS['sem-vinculo-domiciliar'];

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [microInput, setMicroInput] = useState('');
  const [buscaInput, setBuscaInput] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [dias, setDias] = useState(90);
  const [fonte, setFonte] = useState<FonteDados>(getFonteInicial());

  const unidadeParam = unidadeSelecionada?.codunidade;
  // Micro so filtra com 2 digitos; busca so com 3+ caracteres (espelha o backend).
  const microAplicada = /^\d{2}$/.test(microInput) ? microInput : '';
  const buscaAplicada = buscaDebounced.trim().length >= 3 ? buscaDebounced.trim() : '';

  useEffect(() => {
    const t = setTimeout(() => setBuscaDebounced(buscaInput), 400);
    return () => clearTimeout(t);
  }, [buscaInput]);

  useEffect(() => { setPage(1); }, [tipo, microAplicada, buscaAplicada, unidadeParam, dias]);

  useEffect(() => {
    if (!municipioAtivo?.id) return;
    setLoading(true);
    setError(null);
    saudeService
      .getCvatSaneamentoLista(municipioAtivo.id, tipo, {
        quadrimestre, unidade: unidadeParam, micro: microAplicada, busca: buscaAplicada,
        dias: tipo === 'a-vencer' ? dias : undefined, page, limit: 50,
      })
      .then((res: any) => setData(res))
      .catch((err: any) => setError(err.response?.data?.message || 'Erro ao carregar a lista.'))
      .finally(() => setLoading(false));
    // `fonte` entra nas dependencias: trocar o chip tem que refazer a consulta.
  }, [municipioAtivo?.id, tipo, quadrimestre, unidadeParam, microAplicada, buscaAplicada, dias, page, fonte]);

  // CSV e PDF exportam o conjunto FILTRADO inteiro, nao apenas a pagina exibida.
  const filtrosExport = useCallback(() => ({
    quadrimestre, unidade: unidadeParam, micro: microAplicada, busca: buscaAplicada,
    dias: tipo === 'a-vencer' ? dias : undefined,
  }), [quadrimestre, unidadeParam, microAplicada, buscaAplicada, dias, tipo]);

  const exportarCsv = useCallback(() => {
    if (!municipioAtivo?.id) return;
    saudeService
      .exportarCvatSaneamentoCsv(municipioAtivo.id, tipo, filtrosExport())
      .catch(() => setError('Falha ao exportar CSV.'));
  }, [municipioAtivo?.id, tipo, filtrosExport]);

  const exportarPdf = useCallback(() => {
    if (!municipioAtivo?.id) return;
    setError(null);
    saudeService
      .exportarCvatSaneamentoPdf(municipioAtivo.id, tipo, filtrosExport())
      .catch(() => setError('Falha ao gerar o PDF.'));
  }, [municipioAtivo?.id, tipo, filtrosExport]);

  if (!municipioAtivo?.id) {
    return <Box sx={{ p: 3 }}><Alert severity="info">Selecione um município.</Alert></Box>;
  }

  const pacientes = (data?.pacientes || []) as any[];
  const total = data?.paginacao?.total || 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 1 }}>
        <Box>
          <Button component={RouterLink} to="/saude/cvat" size="small" startIcon={<ArrowBackIcon />} sx={{ mb: 0.5 }}>
            Voltar ao CVAT
          </Button>
          <Typography variant="h5" fontWeight={700}>{meta.titulo}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 900 }}>
            {meta.descricao}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {municipioAtivo?.id && (
            <FonteDadosSelector
              municipioId={municipioAtivo.id}
              fonte={fonte}
              onChange={setFonte}
              quadrimestre={quadrimestre}
              painel="cvat-saneamento"
            />
          )}
          <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={exportarCsv} disabled={loading || total === 0}>
            CSV
          </Button>
          <Button variant="outlined" size="small" color="error" startIcon={<PictureAsPdfIcon />} onClick={exportarPdf} disabled={loading || total === 0}>
            PDF
          </Button>
        </Box>
      </Box>

      {/* Troca rapida entre os 4 universos, sem voltar ao painel */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {Object.entries(TIPOS).map(([k, v]) => (
          <Chip
            key={k}
            label={v.titulo}
            onClick={() => navigate(`/saude/cvat/lista?tipo=${k}${quadrimestre ? `&quadrimestre=${quadrimestre}` : ''}`)}
            color={k === tipo ? 'primary' : 'default'}
            variant={k === tipo ? 'filled' : 'outlined'}
            size="small"
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField size="small" label="Buscar nome, CPF ou CNS" value={buscaInput}
          onChange={(e) => setBuscaInput(e.target.value)} sx={{ minWidth: 260 }}
          helperText={buscaInput.length > 0 && buscaInput.length < 3 ? 'Digite ao menos 3 caracteres' : ' '} />
        <TextField size="small" label="Micro" value={microInput}
          onChange={(e) => setMicroInput(e.target.value.replace(/\D/g, '').slice(0, 2))} sx={{ width: 100 }}
          helperText={microInput.length === 1 ? '2 dígitos' : ' '} />
        {tipo === 'a-vencer' && (
          <TextField select size="small" label="Janela" value={dias} onChange={(e) => setDias(Number(e.target.value))} sx={{ width: 150 }}>
            {[30, 60, 90, 180].map((d) => <MenuItem key={d} value={d}>{d} dias</MenuItem>)}
          </TextField>
        )}
        {unidadeSelecionada && <Chip size="small" variant="outlined" label={unidadeSelecionada.nome} />}
        <Chip size="small" color="primary" variant="outlined" label={`${total.toLocaleString('pt-BR')} pessoas`} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

      {!loading && pacientes.length === 0 && !error && (
        <Alert severity="success">Nenhuma pendência encontrada com os filtros atuais.</Alert>
      )}

      {!loading && pacientes.length > 0 && (
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell align="center">Idade</TableCell>
                  <TableCell>Equipe</TableCell>
                  <TableCell align="center">Micro</TableCell>
                  <TableCell>Endereço</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell>Telefone</TableCell>
                  {tipo === 'sem-acompanhamento' ? (
                    <>
                      <TableCell align="center">Contatos</TableCell>
                      <TableCell>Situação</TableCell>
                      <TableCell align="center">Peso</TableCell>
                    </>
                  ) : tipo === 'identificacao-incompleta' ? (
                    <TableCell>Inconsistência</TableCell>
                  ) : (
                    <>
                      <TableCell align="center">Cadastro</TableCell>
                      <TableCell align="center">Vence em</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {pacientes.map((p) => (
                  <TableRow key={p.cid} hover>
                    <TableCell sx={{ maxWidth: 240 }}>
                      <Typography variant="body2" fontWeight={600} noWrap title={p.no_cidadao}>{p.no_cidadao || '—'}</Typography>
                    </TableCell>
                    <TableCell align="center">{p.idade ?? '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 190 }}>
                      <Typography variant="caption" noWrap title={p.equipe} component="div">{p.equipe || '—'}</Typography>
                      {p.ine && <Typography variant="caption" color="text.secondary">INE {p.ine}</Typography>}
                    </TableCell>
                    <TableCell align="center">{p.nu_micro_area || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="caption" noWrap title={p.endereco} component="div">{p.endereco || '—'}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{maskCpf(p.nu_cpf_cidadao)}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{p.nu_telefone_celular || '—'}</Typography></TableCell>

                    {tipo === 'sem-acompanhamento' ? (
                      <>
                        <TableCell align="center">{p.contatos_periodo ?? 0}</TableCell>
                        <TableCell><Typography variant="caption">{p.motivo}</Typography></TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={Number(p.peso_vulnerabilidade).toFixed(1)}
                            sx={{
                              fontWeight: 700,
                              bgcolor: alpha(Number(p.peso_vulnerabilidade) >= 2.5 ? '#c62828' : Number(p.peso_vulnerabilidade) > 1 ? '#e65100' : theme.palette.text.primary, 0.12),
                              color: Number(p.peso_vulnerabilidade) >= 2.5 ? '#c62828' : Number(p.peso_vulnerabilidade) > 1 ? '#e65100' : 'text.secondary',
                            }} />
                        </TableCell>
                      </>
                    ) : tipo === 'identificacao-incompleta' ? (
                      <TableCell><Typography variant="caption">{p.motivo}</Typography></TableCell>
                    ) : (
                      <>
                        <TableCell align="center"><Typography variant="caption">{formatDate(p.dt_ficha)}</Typography></TableCell>
                        <TableCell align="center">
                          <Tooltip title={`Vence em ${formatDate(p.vence_em)}`}>
                            <Chip size="small"
                              icon={tipo === 'sem-vinculo-domiciliar' ? <HomeWorkIcon sx={{ fontSize: 13 }} /> : undefined}
                              label={p.dias_para_vencer != null ? `${p.dias_para_vencer} d` : formatDate(p.vence_em)}
                              sx={{
                                bgcolor: alpha(Number(p.dias_para_vencer) <= 90 ? '#c62828' : '#1565c0', 0.1),
                                color: Number(p.dias_para_vencer) <= 90 ? '#c62828' : '#1565c0',
                                fontWeight: 600,
                              }} />
                          </Tooltip>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {data?.paginacao?.total_paginas > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <Pagination count={data.paginacao.total_paginas} page={page} onChange={(_, v) => setPage(v)}
                showFirstButton showLastButton size="small" />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SaudeCvatLista;
